import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import Head from 'next/head';

// Utils
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { isArray, isNil } from 'lodash';
import { useRouter } from 'next/router';
import parseSearchQuery from '../../src/utils/search/parseSearchQuery';
import packageUtils from '../../src/utils/package/packageUtils';
import bookingStep from '../../src/enum/bookingStep';
import { useAppContext } from '../../src/store/AppContext';
import packagesTypes from '../../src/enum/packagesTypes';
import getLanguageId from '../../src/utils/i18n/getLanguageId';
import useCart from '../../src/hooks/useCart';
import useDataLayer from '../../src/hooks/useDataLayer';
import sanitizeHtml from '../../src/utils/wysiwyg/sanitizeHtml';
import useImage from '../../src/hooks/useImage';
import useReplaceCartFromUrl from '../../src/hooks/useReplaceCartFromUrl';

// Icons
import CheckIcon from '../../public/icons/check.svg';
import ArrowLeft from '../../public/icons/chevron.svg';

// Components
import Button from '../../components/atoms/Button/Button';
import Flag from '../../components/atoms/Flag/Flag';
import SlidesBanner from '../../components/organisms/SlidesBanner/SlidesBanner';
import StickyTopBar from '../../components/camp/StickyTopBar/StickyTopBar';
import CampSection from '../../components/camp/CampSection/CampSection';
import Offers from '../../components/organisms/SearchResults/PackageCard/Offers/Offers';
import MobileSlidesBanner from '../../components/organisms/MobileSlidesBanner/MobileSlidesBanner';
// eslint-disable-next-line max-len
import WarningFixedOptions from '../../components/organisms/SearchResults/PackageCard/WarningFixedOptions/WarningFixedOptions';
import ContactBanner from '../../components/organisms/ContactBanner/ContactBanner';
import LightboxImages from '../../components/organisms/LightboxImages/LightboxImages';
import ReplaceCartModal from '../../components/book/ReplaceCartModal/ReplaceCartModal';

// Services
import packagesService from '../../src/services/packagesService';
import offersService from '../../src/services/offersService';
import extrasService from '../../src/services/extrasService';

import styles from '../../styles/pages/CampDetails.module.scss';

const BookingTunnel = dynamic(() => import('../../components/layout/BookingTunnel/BookingTunnel'), { ssr: false });

function CampDetails({
  campPackage, formats, types, allExtras,
}) {
  const { t } = useTranslation(['common']);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const isTablet = useMediaQuery({ maxWidth: 1024 });
  const isSmallLaptop = useMediaQuery({ maxWidth: 1200 });
  const router = useRouter();

  const lightboxRef = useRef();

  const { getPlayerLabel } = useCart();
  const { gtmEventViewProduct } = useDataLayer();
  const { getImageSrc } = useImage();
  const { handleReplaceCartFromUrl } = useReplaceCartFromUrl();

  const [{ players, currentPlayer, previousPath }, stateDispatch] = useAppContext();

  const addCampTarget = useRef('addCampTarget');
  const campTitle = useRef();

  const [showTopBar, setShowTopBar] = useState(false);

  const lightboxImages = useMemo(() => (
    campPackage?.slides?.length ? (
      campPackage.slides.map((slide) => (
        getImageSrc({
          fileId: slide.path,
          widths: {
            default: 1064,
            smallLaptop: 920,
            tablet: 744,
            mobile: 748,
          },
        })
      ))
    ) : []
  ), [campPackage, isMobile, isTablet, isSmallLaptop]);

  const handleAddCamp = () => {
    const targetTop = addCampTarget?.current?.offsetTop;
    if (targetTop) window.scrollTo(0, targetTop - 100);
  };

  const handleStickyTopBar = () => {
    if (campTitle?.current) {
      const isUnderTitle = isTablet ? true : window.scrollY > (campTitle.current.offsetTop - 50);
      const footer = document.querySelector('#footer');
      const halfFooterHeight = footer ? footer.clientHeight / 2 : 0;
      const isAboveFooter = footer && isTablet ? (
        (window.scrollY + window.innerHeight - halfFooterHeight) < footer.offsetTop
      ) : true;

      setShowTopBar(isUnderTitle && isAboveFooter);
    }
  };

  const handleNewCart = () => {
    // if the user lands on this page and does not have an existing cart
    // create a new cart based on the package

    if (!players && !isNil(campPackage?.typeId) && router.query && Object.keys(router.query)?.length) {
      const type = packagesTypes.find((packType) => packType.id === campPackage.typeId);
      const parsedQuery = parseSearchQuery(router.query);
      const newPlayer = {
        id: 1,
        typeId: campPackage.typeId,
        completed: false,
        typeLabel: type.label,
        stage: null,
        options: null,
        otherOptions: null,
      };

      if (type && parsedQuery) {
        stateDispatch({
          type: 'SET_SEARCH',
          payload: {
            weeks: parsedQuery.weeks,
            weekend: parsedQuery.weekend || false,
            hosting: true,
            child: campPackage.typeId === 1 ? 1 : 0,
            adult: campPackage.typeId === 2 ? 1 : 0,
            baby: campPackage.typeId === 3 ? 1 : 0,
          },
        });
        stateDispatch({ type: 'SET_PLAYERS', payload: [newPlayer] });
        stateDispatch({ type: 'SET_CURRENT_PLAYER', payload: newPlayer });
      }
    }
  };

  useEffect(() => {
    // Hide "Summer Tour 1" and "Summer Tour 3" (production ids : 19, 31)
    if (campPackage?.id === 19 || campPackage?.id === 31) {
      router.push('/');
    }

    handleStickyTopBar();
    window.addEventListener('scroll', handleStickyTopBar, true);

    handleReplaceCartFromUrl();
    handleNewCart();

    if (campPackage) {
      gtmEventViewProduct(
        {
          product: campPackage,
          productFormat: formats[campPackage.formatId]?.name[getLanguageId('en')],
          productType: types[campPackage.typeId]?.name[getLanguageId('en')],
          minPrice: packageUtils.getLowestOfferPrice(
            campPackage.offers?.length ? campPackage.offers : campPackage.otherOffers,
          ),
          player: currentPlayer ? getPlayerLabel({
            typeLabel: currentPlayer.typeLabel,
            typeId: currentPlayer.typeId,
            playerId: currentPlayer.id,
          }) : null,
        },
      );
    }

    return () => window.removeEventListener('scroll', handleStickyTopBar, true);
  }, []);

  useEffect(() => {
    window.removeEventListener('scroll', handleStickyTopBar, true);
    window.addEventListener('scroll', handleStickyTopBar, true);
  }, [isTablet]);

  const getBackLabel = () => {
    if (previousPath && previousPath === bookingStep.route.summary) {
      return `${t('camp.backToSummary')} ${getPlayerLabel({
        typeLabel: currentPlayer.typeLabel,
        typeId: currentPlayer.typeId,
        playerId: currentPlayer.id,
      })}`;
    }
    return t('common.back');
  };

  return (campPackage ? (
    <>
      <Head>
        <title>
          {campPackage?.name[getLanguageId(router.locale)]}
        </title>
      </Head>
      <BookingTunnel
        reverseBackground
        noStepper={!(campPackage.offers?.length) && !(campPackage.otherOffers?.length)}
        isStagePage
        mobileNoPadding
        backToPrev={getBackLabel()}
        hideMobileNextButton
        isFixedStepperFolded={showTopBar}
      >
        <StickyTopBar
          pack={campPackage}
          handleAddCamp={handleAddCamp}
          showTopBar={showTopBar}
          formats={formats}
          types={types}
        />

        {campPackage.slides?.length ? (
          <div className="m-b-30 p-relative">
            {!isMobile ? (
              <SlidesBanner slides={campPackage.slides} lightboxRef={lightboxRef} slidesImages={lightboxImages} />
            ) : (
              <>
                <MobileSlidesBanner slides={campPackage.slides} />
                <button
                  type="button"
                  className={`${styles.mobile_back} flex center`}
                  onClick={() => router.back()}
                >
                  <ArrowLeft />
                </button>
              </>
            )}
          </div>
        ) : null}

        <div className="mobile-p-r-20 mobile-p-l-20">
          <div className="flex a-center j-spb">
            <h1 ref={campTitle} className="fs-30 fw-700 text-c-primary mobile-fs-24">
              {campPackage.name[getLanguageId(router.locale)]}
            </h1>

            {(campPackage.offers?.length || campPackage.otherOffers?.length) && !isMobile ? (
              <div className="p-l-20 fs-24 fw-700 flex a-center j-end ws-nowrap">
                {`${t('package.priceFrom')} 
              ${packageUtils.getLowestOfferPrice(
                  campPackage.offers?.length ? campPackage.offers : campPackage.otherOffers,
                )} €`}

                <WarningFixedOptions pack={campPackage} allExtras={allExtras} locale={router.locale} />
              </div>
            ) : null}
          </div>

          <div className="flex a-center j-spb m-t-15">
            <div className="flex a-center">
              {types && Object.values(types)?.length ? (
                <Flag fontSize="fs-14" className="m-r-10" color="primaryLight" larger>
                  {types[campPackage.typeId]?.name[getLanguageId(router.locale)]}
                </Flag>
              ) : null}
              {formats && Object.values(formats)?.length ? (
                <Flag fontSize="fs-14" color="primaryLight" larger>
                  {formats[campPackage.formatId]?.name[getLanguageId(router.locale)]}
                </Flag>
              ) : null }
            </div>

            {(campPackage.offers?.length || campPackage.otherOffers?.length) && !isMobile ? (
              <div className={styles.add_btn}>
                <Button text="uppercase" onClick={handleAddCamp}>{t('common.add')}</Button>
              </div>
            ) : null}
          </div>

          {isMobile ? (
            <p
              className="fw-300 m-t-20 fs-14"
              dangerouslySetInnerHTML={{ __html: campPackage.fullDescription[getLanguageId(router.locale)] }}
            />
          ) : null}

          <div className={styles.mobile_reverse}>
            <CampSection title={t('camp.details.description.title')} classNames="m-t-40 mobile-m-t-20">
              <div className={styles.description}>
                {!isMobile ? (
                  <>
                    <h3 className="fs-20 fw-500 text-c-primary m-b-20">
                      {campPackage.subtitle[getLanguageId(router.locale)]}
                    </h3>

                    {campPackage.fullDescription?.[getLanguageId(router.locale)] ? (
                      <div
                        className="fw-400 wysiwyg black bullet_list"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(campPackage.fullDescription[getLanguageId(router.locale)]),
                        }}
                      />
                    ) : null}
                  </>
                ) : null}
                <ul className="flex wrap m-t-15">
                  {campPackage.features.map((feature, i) => (
                    <li key={i} className="m-r-15 m-t-5 fs-14 fw-500 mobile-m-t-10">
                      <CheckIcon className="text-c-secondary m-r-10" />
                      {feature.name[getLanguageId(router.locale)]}
                    </li>
                  ))}
                </ul>
              </div>
            </CampSection>

            <div className={styles.add_camp_target} ref={addCampTarget} />

            {campPackage.offers?.length || campPackage.otherOffers?.length ? (
              <CampSection title={t('camp.details.description.dates')} classNames="m-t-40 mobile-m-t-20" mobileNoLayout>
                <div className={styles.offers_wrapper}>
                  <Offers pack={campPackage} />
                </div>
              </CampSection>
            ) : null}
          </div>

          {campPackage.videoTitle && campPackage.videoSource ? (
            <CampSection
              title={t('camp.details.resortLocation')}
              classNames={`${(campPackage.offers?.length || campPackage.otherOffers?.length) ? 'm-t-0' : 'm-t-40'}`}
            >
              <div className="m-t-40 mobile-m-t-20">
                <h3 className="m-b-20 fs-20 fw-500">
                  {campPackage.videoTitle[getLanguageId(router.locale)]}
                </h3>
                <iframe
                  width="100%"
                  height="400"
                  src={`${campPackage.videoSource}?rel=0&modestbranding=1`}
                  title={campPackage.videoTitle[getLanguageId(router.locale)]}
                  frameBorder="0"
                  allowFullScreen
                  className={styles.iframe}
                />
              </div>
            </CampSection>
          ) : null}

          <CampSection classNames="m-t-30 m-b-50">
            <ContactBanner />
          </CampSection>

        </div>
      </BookingTunnel>

      {lightboxImages?.length ? (
        <LightboxImages
          ref={lightboxRef}
          images={lightboxImages}
        />
      ) : null}

      <ReplaceCartModal />
    </>
  ) : null
  );
}

CampDetails.propTypes = {
  campPackage: PropTypes.shape({}),
};

CampDetails.defaultProps = {
  campPackage: null,
};

export async function getServerSideProps({ query, locale }) {
  const campPackage = query.id ? await packagesService.getPackage(query.id) : null;
  const offers = await offersService.getOffers();
  const offersData = offers ? offers.data : [];

  if (!campPackage || !campPackage.data) {
    return {
      redirect: {
        destination: bookingStep.route.stage,
        permanent: false,
      },
    };
  }

  const parsedQuery = query.weeks ? parseSearchQuery({ weeks: query.weeks }) : null;
  if (parsedQuery?.weeks && !isArray(parsedQuery.weeks)) parsedQuery.weeks = [parsedQuery.weeks];

  const populatedPack = packageUtils.populatePackageOffers(campPackage.data, parsedQuery?.weeks, offersData?.offers);

  // more data
  const formats = await packagesService.getFormats();
  const formatsData = formats ? formats.data : [];
  const types = await packagesService.getTypes();
  const typesData = types ? types.data : [];

  const allExtras = await extrasService.getExtras();
  const allExtrasData = allExtras ? allExtras.data : [];

  return {
    props: {
      ...(await serverSideTranslations(
        locale || 'fr',
        ['common'],
      )),
      campPackage: populatedPack,
      formats: formatsData?.formats || [],
      types: typesData?.types || [],
      allExtras: allExtrasData?.extras || null,
    },
  };
}

export default CampDetails;
